<script setup>
/**
 * LandingScreenshotFrame — browser-style frame around real product
 * screenshots, with auto-rotating carousel of images.
 *
 * Design moves:
 *  - macOS-style traffic light dots with proper red/yellow/green colour
 *    (not the dead grey of the old placeholder).
 *  - URL pill in the chrome bar, mono small, with a tiny lock glyph and
 *    a refresh affordance to the right. URL changes per active image to
 *    reinforce "this is the real product running at different routes".
 *  - Rounded corners, multi-layer drop shadow with subtle cyan halo in
 *    dark mode. The frame itself is the depth/premium cue, not a card.
 *  - Cross-fade transition between images (450 ms) so swaps feel like
 *    "the screen refreshed", not "the page changed".
 *  - Tiny progress dots at bottom-right inside the frame, one per image
 *    in the current set — fills cyan as it advances.
 *
 * Driven by the parent (ProductTabs) which passes a fresh `images`
 * array on every tab change. The internal index resets to 0 whenever
 * the array reference changes, so each tab starts on its first image.
 */
import { ref, computed, onMounted, onUnmounted, watch } from "vue";

const props = defineProps({
  // Array of { src, alt, url } objects to cycle through.
  images: { type: Array, required: true },
  // Time per image in ms.
  rotateMs: { type: Number, default: 4200 },
});

const idx = ref(0);
const active = computed(() => props.images[idx.value] || props.images[0]);

let timer;
const tick = () => {
  if (props.images.length <= 1) return;
  idx.value = (idx.value + 1) % props.images.length;
};
const start = () => {
  stop();
  if (props.images.length > 1) {
    timer = setInterval(tick, props.rotateMs);
  }
};
const stop = () => {
  if (timer) clearInterval(timer);
  timer = null;
};

// Reset when the images array changes (parent switched tabs)
watch(
  () => props.images,
  () => {
    idx.value = 0;
    start();
  }
);

onMounted(start);
onUnmounted(stop);
</script>

<template>
  <div class="relative">
    <!-- Ambient cyan halo behind the frame, only visible in dark mode.
         Sized larger than the frame so it bleeds beyond the corners. -->
    <div
      class="absolute -inset-6 rounded-3xl pointer-events-none opacity-0 dark:opacity-100 transition-opacity"
      :style="{
        background: 'radial-gradient(ellipse at center, hsl(184 100% 50% / 0.12) 0%, transparent 65%)',
      }"
    />

    <figure
      class="relative w-full rounded-xl overflow-hidden bg-card border border-border"
      :style="{
        boxShadow: [
          '0 1px 2px hsl(214 28% 30% / 0.06)',
          '0 8px 24px hsl(214 28% 30% / 0.10)',
          '0 32px 64px hsl(214 28% 30% / 0.14)',
        ].join(', '),
      }"
    >
      <!-- Browser chrome strip -->
      <header
        class="flex items-center gap-3 px-4 py-2.5 border-b border-border"
        :style="{
          background:
            'linear-gradient(180deg, hsl(var(--muted) / 0.85) 0%, hsl(var(--muted) / 0.65) 100%)',
        }"
      >
        <!-- Traffic lights (proper colour, not dead grey) -->
        <div class="flex items-center gap-1.5 flex-shrink-0">
          <span
            class="w-3 h-3 rounded-full"
            :style="{ background: 'radial-gradient(circle at 30% 30%, #ff7c75, #e0443e)' }"
          />
          <span
            class="w-3 h-3 rounded-full"
            :style="{ background: 'radial-gradient(circle at 30% 30%, #ffd58a, #f1b138)' }"
          />
          <span
            class="w-3 h-3 rounded-full"
            :style="{ background: 'radial-gradient(circle at 30% 30%, #88e08f, #28be46)' }"
          />
        </div>

        <!-- URL pill — centered-ish, fixed-height, lock + URL + reload glyph -->
        <div
          class="flex-1 flex items-center gap-2 px-3 py-1 rounded-md bg-background/70 border border-border/50 min-w-0"
        >
          <!-- Small lock icon -->
          <svg
            class="w-3 h-3 text-muted-foreground flex-shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <span class="text-[11px] font-mono text-muted-foreground truncate flex-1 leading-none">
            {{ active.url }}
          </span>
          <!-- Reload glyph -->
          <svg
            class="w-3 h-3 text-muted-foreground/70 flex-shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
            <path d="M3 3v5h5" />
          </svg>
        </div>

        <!-- Right side: page actions (decorative dots) -->
        <div class="flex items-center gap-1 flex-shrink-0">
          <span class="w-1 h-1 rounded-full bg-muted-foreground/40" />
          <span class="w-1 h-1 rounded-full bg-muted-foreground/40" />
          <span class="w-1 h-1 rounded-full bg-muted-foreground/40" />
        </div>
      </header>

      <!-- Image area: layered images, only the active one is opaque.
           Avoids the URL/image desync that <Transition mode="out-in">
           caused (URL flipped before the image swapped in). All images
           live in the DOM simultaneously — they'd be preloaded for the
           carousel anyway. Aspect lock 16/10 matches capture size. -->
      <div class="relative bg-card" style="aspect-ratio: 16 / 10;">
        <img
          v-for="(img, i) in images"
          :key="img.src"
          :src="img.src"
          :alt="img.alt"
          :class="[
            'absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500',
            i === idx ? 'opacity-100' : 'opacity-0',
          ]"
          decoding="async"
        />

        <!-- Progress dots — only if more than one image -->
        <div
          v-if="images.length > 1"
          class="absolute bottom-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/40 backdrop-blur-sm"
        >
          <span
            v-for="(_, i) in images"
            :key="i"
            :class="[
              'w-1.5 h-1.5 rounded-full transition-all',
              i === idx ? 'bg-cyan-300 w-4' : 'bg-white/40',
            ]"
          />
        </div>
      </div>
    </figure>
  </div>
</template>

